import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { api, uploadUrl } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import Icon from '../components/Icon';
import './JournalComptable.css';

// ─── CONSTANTES ──────────────────────────────────────────────────────────────

const CATEGORIES = [
  'Revenu (Revenue)',
  'Dépense (Expense)',
  'Mouvement interne (Internal Transfer)',
];

const SOUS_CATEGORIES = {
  'Revenu (Revenue)': [
    'Vente Woocommerce (Online Order)',
    'Vente directe (Direct Sale)',
  ],
  'Dépense (Expense)': [
    'Équipement (Equipment)',
    'Achat viande / intrants (Meat/Ingredients)',
    'Frais administratifs (Admin)',
    'Repas/représentation (Meals & Entertainment)',
  ],
  'Mouvement interne (Internal Transfer)': [],
};

const LOCALISATIONS = ['Cash LCU', 'Cash ELA', 'Compte LCU', 'Compte ELA', 'Compte Escamel'];
const ASSOCIES      = ['LCU', 'ELA', 'ESCAMEL'];
const MOIS_LABELS   = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const PIE_COLORS    = ['var(--accent)', 'var(--warning)', 'var(--danger)', 'var(--info)', 'var(--success)', 'var(--muted)'];

const ANNEE_COURANTE = new Date().getFullYear();

function fmt(n) {
  if (n === null || n === undefined) return '—';
  const abs = Math.abs(n).toFixed(2).replace(/\.00$/, '');
  return (n < 0 ? '-' : '') + abs + ' $';
}
function fmtDate(d) {
  if (!d) return '—';
  return d.slice(0, 10);
}

// ─── COMPOSANT PRINCIPAL ─────────────────────────────────────────────────────

export default function JournalComptable() {
  // ─ Données
  const [transactions, setTransactions]   = useState([]);
  const [sommaire, setSommaire]           = useState({ revenus: 0, depenses: 0, mouvements: 0, net: 0, nb_transactions: 0 });
  const [total, setTotal]                 = useState(0);
  const [pages, setPages]                 = useState(1);
  const [encaisse, setEncaisse]           = useState({ soldes: [], total_encaisse: 0 });
  const [resume, setResume]               = useState(null);
  const [fournisseurs, setFournisseurs]   = useState([]);

  // ─ Filtres
  const [annee, setAnnee]                 = useState(String(ANNEE_COURANTE));
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [filtreAssocie, setFiltreAssocie]   = useState('');
  const [filtreMois, setFiltreMois]         = useState('');
  const [filtreDestination, setFiltreDestination] = useState('');
  const [filtreFournisseur, setFiltreFournisseur] = useState('');
  const [recherche, setRecherche]           = useState('');
  const [page, setPage]                     = useState(1);

  // ─ Modals
  const [showTx, setShowTx]               = useState(false);
  const [showMouvement, setShowMouvement] = useState(false);
  const [showSoldes, setShowSoldes]       = useState(false);
  const [showImport, setShowImport]       = useState(false);
  const [editTx, setEditTx]               = useState(null);

  const [loading, setLoading]             = useState(false);
  const [toast, setToast]                 = useState(null);

  // ─── CHARGEMENTS ─────────────────────────────────────────────────────────

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (annee)            params.set('annee', annee);
      if (filtreCategorie)  params.set('categorie', filtreCategorie);
      if (filtreAssocie)    params.set('associe', filtreAssocie);
      if (filtreMois)       params.set('mois', filtreMois);
      if (filtreDestination) params.set('destination', filtreDestination);
      if (filtreFournisseur) params.set('fournisseur', filtreFournisseur);
      if (recherche)        params.set('q', recherche);

      const data = await api.get(`/journal?${params}`);
      setTransactions(data.transactions);
      setSommaire(data.sommaire);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setLoading(false);
    }
  }, [annee, filtreCategorie, filtreAssocie, filtreMois, filtreDestination, filtreFournisseur, recherche, page]);

  const loadEncaisse = useCallback(async () => {
    try {
      const data = await api.get('/encaisse/soldes');
      setEncaisse(data);
    } catch {}
  }, []);

  const loadResume = useCallback(async () => {
    try {
      const data = await api.get(`/journal/resume/${annee}`);
      setResume(data);
    } catch {}
  }, [annee]);

  const loadFournisseurs = useCallback(async () => {
    try {
      const data = await api.get('/journal/fournisseurs');
      setFournisseurs(data);
    } catch {}
  }, []);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);
  useEffect(() => { loadEncaisse(); }, [loadEncaisse]);
  useEffect(() => { loadResume(); }, [loadResume]);
  useEffect(() => { loadFournisseurs(); }, [loadFournisseurs]);

  // Reset page quand filtres changent
  useEffect(() => { setPage(1); }, [annee, filtreCategorie, filtreAssocie, filtreMois, filtreDestination, filtreFournisseur, recherche]);

  // ─── HELPERS ─────────────────────────────────────────────────────────────

  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }

  function refresh() {
    loadTransactions();
    loadEncaisse();
    loadResume();
    loadFournisseurs();
  }

  async function supprimerTx(id) {
    if (!window.confirm('Supprimer cette transaction ?')) return;
    try {
      const res = await api.delete(`/journal/${id}`);
      if (res.avertissement) showToast('warn', res.avertissement);
      else showToast('success', 'Transaction supprimée');
      refresh();
    } catch (err) {
      showToast('error', err.message || err.response?.data?.error || 'Erreur suppression');
    }
  }

  // ─── RENDU ───────────────────────────────────────────────────────────────

  // Données graphiques
  const dataBarChart = Array.from({ length: 12 }, (_, i) => {
    const mois = i + 1;
    const found = resume?.par_mois?.find(m => m.mois === mois);
    return {
      name: MOIS_LABELS[i],
      Revenus: found ? found.revenus : 0,
      Dépenses: found ? Math.abs(found.depenses) : 0,
    };
  });

  const dataPie = resume ? [
    { name: 'Viande/Intrants', value: Math.abs(resume.depenses?.viande_intrants || 0) },
    { name: 'Équipement',      value: Math.abs(resume.depenses?.equipement || 0) },
    { name: 'Admin',           value: Math.abs(resume.depenses?.admin || 0) },
    { name: 'Repas (50%)',     value: Math.abs(resume.depenses?.repas || 0) },
    { name: 'Autres',          value: Math.abs((resume.depenses?.total || 0) - (resume.depenses?.viande_intrants || 0) - (resume.depenses?.equipement || 0) - (resume.depenses?.admin || 0) - (resume.depenses?.repas || 0)) },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="journal-page">
      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.message}</div>
      )}

      {/* ── HEADER ───────────────────────────────────────────────────── */}
      <div className="journal-header">
        <div className="journal-header-text">
          <h1 className="page-title">Journal Comptable</h1>
          <p className="page-subtitle muted">Escamel Artisans du Fumoir S.E.N.C.</p>
        </div>
      </div>

      {/* ── ENCAISSE ─────────────────────────────────────────────────── */}
      <EncaisseSection
        encaisse={encaisse}
        onConfigurer={() => setShowSoldes(true)}
        onMouvement={() => setShowMouvement(true)}
      />

      {/* ── SOMMAIRE BARRE ───────────────────────────────────────────── */}
      <div className="journal-sommaire">
        <select className="journal-annee-sel" value={annee} onChange={e => setAnnee(e.target.value)}>
          {[2025, 2026, 2027].map(a => <option key={a} value={a}>FY{a}</option>)}
        </select>
        <div className="sommaire-sep" />
        <div className="sommaire-stat">
          <span className="sommaire-stat-label">Revenus</span>
          <span className="sommaire-stat-val success">{fmt(sommaire.revenus)}</span>
        </div>
        <div className="sommaire-stat">
          <span className="sommaire-stat-label">Dépenses</span>
          <span className="sommaire-stat-val danger">{fmt(sommaire.depenses)}</span>
        </div>
        <div className="sommaire-stat">
          <span className="sommaire-stat-label">Net</span>
          <span className={`sommaire-stat-val ${sommaire.net >= 0 ? 'success' : 'danger'}`}>{fmt(sommaire.net)}</span>
        </div>
        <div className="sommaire-sep" />
        <div className="sommaire-stat">
          <span className="sommaire-stat-label">Transactions</span>
          <span className="sommaire-stat-val">{sommaire.nb_transactions}</span>
        </div>
      </div>

      {/* ── FILTRES + ACTIONS ─────────────────────────────────────────── */}
      <div className="journal-filtres card">
        <div className="filtres-top">
          <div className="filtres-tabs">
            {['', 'Revenu (Revenue)', 'Dépense (Expense)', 'Mouvement interne (Internal Transfer)'].map((c, i) => (
              <button
                key={c}
                className={`btn-tab ${filtreCategorie === c ? 'active' : ''}`}
                onClick={() => setFiltreCategorie(c)}
              >
                {['Tous', 'Revenus', 'Dépenses', 'Mouvements'][i]}
              </button>
            ))}
          </div>
          <div className="filtres-actions">
            <button className="btn btn-primary" onClick={() => { setEditTx(null); setShowTx(true); }}>
              <Icon name="ajouter" size={14} /> Nouvelle transaction
            </button>
            <button className="btn btn-ghost" onClick={() => setShowMouvement(true)}>
              <Icon name="encaisse" size={14} /> Mouvement
            </button>
            <button className="btn btn-ghost" onClick={() => setShowImport(true)}>
              <Icon name="televerser" size={14} /> Import CSV
            </button>
          </div>
        </div>
        <div className="filtres-row">
          <select value={filtreAssocie} onChange={e => setFiltreAssocie(e.target.value)}>
            <option value="">Associé</option>
            {ASSOCIES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filtreMois} onChange={e => setFiltreMois(e.target.value)}>
            <option value="">Mois</option>
            {MOIS_LABELS.map((m, i) => <option key={i+1} value={String(i+1).padStart(2,'0')}>{m}</option>)}
          </select>
          <select value={filtreDestination} onChange={e => setFiltreDestination(e.target.value)}>
            <option value="">Destination</option>
            {LOCALISATIONS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={filtreFournisseur} onChange={e => setFiltreFournisseur(e.target.value)}>
            <option value="">Fournisseur</option>
            {fournisseurs.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <div className="filtre-search">
            <span className="filtre-search-icon"><Icon name="recherche" size={14} /></span>
            <input
              placeholder="Rechercher..."
              value={recherche}
              onChange={e => setRecherche(e.target.value)}
            />
          </div>
          {(filtreAssocie || filtreMois || filtreDestination || filtreFournisseur || recherche) && (
            <button className="btn btn-ghost btn-sm" onClick={() => {
              setFiltreAssocie(''); setFiltreMois(''); setFiltreDestination('');
              setFiltreFournisseur(''); setRecherche('');
            }}><Icon name="fermer" size={12} /> Réinitialiser</button>
          )}
        </div>
      </div>

      {/* ── TABLEAU ───────────────────────────────────────────────────── */}
      <div className="card">
        {loading ? (
          <div className="journal-loading">
            <div className="skeleton" style={{ width: '100%', height: 40 }} />
            <div className="skeleton" style={{ width: '100%', height: 40 }} />
            <div className="skeleton" style={{ width: '85%', height: 40 }} />
          </div>
        ) : transactions.length === 0 ? (
          <div className="empty-state">
            <Icon name="recu" size={32} />
            <span className="empty-state__titre">Aucune transaction</span>
            <span>Aucune transaction pour ces filtres.</span>
          </div>
        ) : (
          <div className="esc-table-wrap">
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Associé</th>
                  <th>Catégorie</th>
                  <th>Description</th>
                  <th>Fournisseur</th>
                  <th className="text-right">Montant</th>
                  <th>Destination</th>
                  <th>Liens</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <TxRow
                    key={tx.id}
                    tx={tx}
                    onEdit={() => { setEditTx(tx); setShowTx(true); }}
                    onDelete={() => supprimerTx(tx.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="journal-pagination">
            <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹ Précédent</button>
            <span className="muted">Page {page} / {pages} — {total} transactions</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>Suivant ›</button>
          </div>
        )}
      </div>

      {/* ── GRAPHIQUES ────────────────────────────────────────────────── */}
      <div className="journal-graphiques">
        <div className="card graphique-card">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="analytics" size={16} /></span>
              <span className="card-title">Revenus vs Dépenses — FY{annee}</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={dataBarChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--muted)', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8 }}
                formatter={v => [`${v.toFixed(2)} $`]}
              />
              <Legend wrapperStyle={{ color: 'var(--muted)', fontSize: 12 }} />
              <Bar dataKey="Revenus"  fill="var(--success)" radius={[3,3,0,0]} />
              <Bar dataKey="Dépenses" fill="var(--danger)"  radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {dataPie.length > 0 && (
          <div className="card graphique-card">
            <div className="card-header">
              <div className="card-header-left">
                <span className="card-header-icon"><Icon name="donut" size={16} /></span>
                <span className="card-title">Répartition dépenses — FY{annee}</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={dataPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {dataPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8 }}
                  formatter={v => [`${v.toFixed(2)} $`]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── MODALS ────────────────────────────────────────────────────── */}
      {showTx && (
        <ModalTransaction
          tx={editTx}
          fournisseurs={fournisseurs}
          encaisse={encaisse}
          onClose={() => { setShowTx(false); setEditTx(null); }}
          onSuccess={() => { setShowTx(false); setEditTx(null); refresh(); showToast('success', editTx ? 'Transaction modifiée' : 'Transaction créée'); }}
          onError={msg => showToast('error', msg)}
        />
      )}
      {showMouvement && (
        <ModalMouvement
          encaisse={encaisse}
          onClose={() => setShowMouvement(false)}
          onSuccess={() => { setShowMouvement(false); refresh(); showToast('success', 'Mouvement enregistré'); }}
          onError={msg => showToast('error', msg)}
        />
      )}
      {showSoldes && (
        <ModalSoldes
          encaisse={encaisse}
          onClose={() => setShowSoldes(false)}
          onSuccess={() => { setShowSoldes(false); loadEncaisse(); showToast('success', 'Soldes de départ mis à jour'); }}
          onError={msg => showToast('error', msg)}
        />
      )}
      {showImport && (
        <ModalImportCSV
          onClose={() => setShowImport(false)}
          onSuccess={n => { setShowImport(false); refresh(); showToast('success', `${n} transactions importées`); }}
          onError={msg => showToast('error', msg)}
        />
      )}

      {/* FAB mobile — Nouvelle transaction */}
      <button className="fab" onClick={() => { setEditTx(null); setShowTx(true); }} aria-label="Nouvelle transaction" title="Nouvelle transaction">
        <Icon name="ajouter" size={22} />
      </button>
    </div>
  );
}

// ─── SECTION ENCAISSE ────────────────────────────────────────────────────────

function EncaisseSection({ encaisse, onConfigurer, onMouvement }) {
  return (
    <div className="encaisse-section card rise-in">
      <div className="encaisse-header">
        <h2 className="card-title">
          <Icon name="encaisse" size={16} />
          Encaisse Escamel
        </h2>
        <div className="encaisse-total">
          Total :{' '}
          <span className={encaisse.total_encaisse >= 0 ? 'gold' : 'danger'}>
            {fmt(encaisse.total_encaisse)}
          </span>
        </div>
      </div>
      <div className="encaisse-soldes">
        {encaisse.soldes.map(s => (
          <div key={s.localisation} className="encaisse-card">
            <div className="encaisse-loc">{s.localisation}</div>
            <div className={`encaisse-val ${s.solde_actuel >= 0 ? 'gold' : 'danger'}`}>
              {fmt(s.solde_actuel)}
            </div>
            {s.derniere_transaction && (
              <div className="encaisse-date">Dernier : {fmtDate(s.derniere_transaction)}</div>
            )}
          </div>
        ))}
      </div>
      <div className="encaisse-actions">
        <button className="btn btn-ghost btn-sm" onClick={onConfigurer}>
          <Icon name="parametres" size={13} /> Configurer soldes
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onMouvement}>
          <Icon name="encaisse" size={13} /> Nouveau mouvement
        </button>
      </div>
    </div>
  );
}

// ─── LIGNE TABLEAU ────────────────────────────────────────────────────────────

function TxRow({ tx, onEdit, onDelete }) {
  const isMouvement = tx.categorie === 'Mouvement interne (Internal Transfer)';
  const isWC        = !!tx.wc_order_id;
  const isBatch     = !!tx.batch_id;

  function badgeAssocie(a) {
    if (a === 'LCU') return <span className="badge badge-lcu">{a}</span>;
    if (a === 'ELA') return <span className="badge badge-ela">{a}</span>;
    return <span className="badge badge-esc">{a}</span>;
  }

  function catLabel(c) {
    if (c === 'Revenu (Revenue)')                       return <span className="badge badge--success">Revenu</span>;
    if (c === 'Dépense (Expense)')                      return <span className="badge badge--danger">Dépense</span>;
    if (c === 'Mouvement interne (Internal Transfer)')  return <span className="badge" style={{ fontStyle:'italic' }}>Mouvement</span>;
    return <span className="badge">{c}</span>;
  }

  return (
    <tr className={isMouvement ? 'tx-mouvement' : ''}>
      <td data-label="Date">{fmtDate(tx.date_trans)}</td>
      <td data-label="Associé">{badgeAssocie(tx.associe)}</td>
      <td data-label="Catégorie">
        {catLabel(tx.categorie)}
        {tx.sous_categorie && <div className="tx-subcat">{tx.sous_categorie}</div>}
      </td>
      <td data-label="Description" className="tx-desc">{tx.description}</td>
      <td data-label="Fournisseur" className="muted">{tx.fournisseur || '—'}</td>
      <td data-label="Montant" className={`text-right ${tx.montant > 0 ? 'montant-positif' : 'montant-negatif'}`}>
        {fmt(tx.montant)}
      </td>
      <td data-label="Destination" className="muted">{tx.destination || '—'}</td>
      <td data-label="Liens" className="tx-liens">
        {tx.recu_fichier && (
          <a
            href={uploadUrl(`/uploads/recus/${tx.recu_fichier}`)}
            target="_blank"
            rel="noreferrer"
            title="Voir le reçu"
            className="tx-icon"
          ><Icon name="facture" size={15} /></a>
        )}
        {isBatch    && <span className="tx-icon" title={`Batch #${tx.batch_id}`}><Icon name="lien" size={15} /></span>}
        {isWC       && <span className="tx-icon" title={`Commande WC #${tx.wc_order_id}`}><Icon name="panier" size={15} /></span>}
        {tx.mouvement_id && <span className="tx-icon" title="Mouvement lié"><Icon name="encaisse" size={15} /></span>}
      </td>
      <td data-label="Actions" className="tx-actions">
        <button className="btn btn-ghost btn-xs" onClick={onEdit} title="Modifier">
          <Icon name="modifier" size={13} />
        </button>
        {!isWC && (
          <button className="btn btn-danger btn-xs" onClick={onDelete} title="Supprimer">
            <Icon name="supprimer" size={13} />
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── MODAL TRANSACTION ────────────────────────────────────────────────────────

function ModalTransaction({ tx, fournisseurs, encaisse, onClose, onSuccess, onError }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date_trans:     tx?.date_trans?.slice(0,10) || today,
    associe:        tx?.associe        || 'LCU',
    categorie:      tx?.categorie      || 'Dépense (Expense)',
    sous_categorie: tx?.sous_categorie || '',
    description:    tx?.description    || '',
    fournisseur:    tx?.fournisseur    || '',
    montant:        tx ? String(Math.abs(tx.montant)) : '',
    destination:    tx?.destination    || 'Compte LCU',
    annee_fiscale:  tx?.annee_fiscale  || ANNEE_COURANTE,
    numero_recu:    tx?.numero_recu    || '',
    notes:          tx?.notes          || '',
  });
  const [signe, setSigne]   = useState(tx?.montant < 0 ? '-' : '+');
  const [fichier, setFichier] = useState(null);
  const [saving, setSaving]  = useState(false);
  const fileRef              = useRef();

  const sousCategories = SOUS_CATEGORIES[form.categorie] || [];

  // Montant signé calculé
  const montantReel = form.montant ? (signe === '-' ? -Math.abs(parseFloat(form.montant)) : Math.abs(parseFloat(form.montant))) : 0;

  // Impact encaisse
  const soldeActuel = encaisse.soldes.find(s => s.localisation === form.destination)?.solde_actuel ?? null;
  const soldeApres  = soldeActuel !== null ? soldeActuel + montantReel : null;

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function sauvegarder() {
    if (!form.date_trans || !form.associe || !form.categorie || !form.description || !form.montant) {
      return onError('Champs requis manquants');
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null) fd.append(k, v); });
      fd.set('montant', montantReel);
      if (fichier) fd.append('recu', fichier);

      if (tx) {
        await api.putForm(`/journal/${tx.id}`, fd);
      } else {
        await api.postForm('/journal', fd);
      }
      onSuccess();
    } catch (err) {
      onError(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h3>{tx ? 'Modifier la transaction' : 'Nouvelle transaction'}</h3>
          <button className="modal-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
        </div>

        <div className="modal-body modal-grid-2">
          <div className="field">
            <label>Date</label>
            <DateInput value={form.date_trans} onChange={v => set('date_trans', v)} />
          </div>
          <div className="field">
            <label>Associé</label>
            <select value={form.associe} onChange={e => set('associe', e.target.value)}>
              {ASSOCIES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Catégorie</label>
            <select value={form.categorie} onChange={e => { set('categorie', e.target.value); set('sous_categorie', ''); }}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Sous-catégorie</label>
            <select value={form.sous_categorie} onChange={e => set('sous_categorie', e.target.value)} disabled={sousCategories.length === 0}>
              <option value="">—</option>
              {sousCategories.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="field field-full">
            <label>Description *</label>
            <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="ex. Achat épaule de porc chez Costco" />
          </div>
          <div className="field">
            <label>Fournisseur</label>
            <input
              list="fournisseurs-list"
              value={form.fournisseur}
              onChange={e => set('fournisseur', e.target.value)}
              placeholder="ex. Costco"
            />
            <datalist id="fournisseurs-list">
              {fournisseurs.map(f => <option key={f} value={f} />)}
            </datalist>
          </div>
          <div className="field">
            <label>Montant $</label>
            <div className="montant-input">
              <select className="signe-sel" value={signe} onChange={e => setSigne(e.target.value)}>
                <option value="+">+ Revenu</option>
                <option value="-">- Dépense</option>
              </select>
              <input
                type="number" step="0.01" min="0"
                value={form.montant}
                onChange={e => set('montant', e.target.value)}
                placeholder="0.00"
              />
            </div>
            {form.montant && (
              <span className={`badge-montant ${signe === '+' ? 'success' : 'danger'}`}>
                {signe === '+' ? 'REVENU' : 'DÉPENSE'} {fmt(montantReel)}
              </span>
            )}
          </div>
          <div className="field">
            <label>Destination</label>
            <select value={form.destination} onChange={e => set('destination', e.target.value)}>
              <option value="">—</option>
              {LOCALISATIONS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Année fiscale</label>
            <select value={form.annee_fiscale} onChange={e => set('annee_fiscale', e.target.value)}>
              {[2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="field">
            <label>N° reçu</label>
            <input value={form.numero_recu} onChange={e => set('numero_recu', e.target.value)} placeholder="ex. 2024-001" />
          </div>
          <div className="field field-full">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
          </div>
          <div className="field field-full">
            <label>Reçu / Facture</label>
            <div className="upload-zone" onClick={() => fileRef.current?.click()}>
              {fichier
                ? <span className="success"><Icon name="facture" size={14} /> {fichier.name}</span>
                : tx?.recu_fichier
                  ? <span className="muted"><Icon name="facture" size={14} /> Reçu existant — cliquer pour remplacer (<a href={uploadUrl(`/uploads/recus/${tx.recu_fichier}`)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>voir</a>)</span>
                  : <span className="muted">Déposer un reçu ici (jpg, png, pdf)</span>
              }
            </div>
            <input
              type="file" ref={fileRef} style={{ display: 'none' }}
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={e => setFichier(e.target.files[0] || null)}
            />
          </div>
          {soldeActuel !== null && form.destination && form.montant && (
            <div className="field field-full encaisse-impact">
              <span className="muted">Impact encaisse :</span>
              <span> {form.destination} : </span>
              <span className={soldeActuel >= 0 ? 'success' : 'danger'}>{fmt(soldeActuel)}</span>
              <span className="muted"> → </span>
              <span className={soldeApres >= 0 ? 'success' : 'danger'}>{fmt(soldeApres)}</span>
              <span className={montantReel >= 0 ? 'success' : 'danger'}> ({montantReel >= 0 ? '+' : ''}{fmt(montantReel)})</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={sauvegarder} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL MOUVEMENT ─────────────────────────────────────────────────────────

function ModalMouvement({ encaisse, onClose, onSuccess, onError }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date_trans: today, montant: '', de: 'Cash LCU', vers: 'Compte LCU', description: '', notes: '' });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const soldes = {};
  encaisse.soldes.forEach(s => { soldes[s.localisation] = s.solde_actuel; });
  const montant = parseFloat(form.montant) || 0;
  const soldeDe  = soldes[form.de]   ?? 0;
  const soldeVers = soldes[form.vers] ?? 0;
  const aprDe   = soldeDe   - montant;
  const aprVers = soldeVers + montant;

  async function confirmer() {
    if (!form.montant || !form.de || !form.vers) return onError('Champs requis manquants');
    setSaving(true);
    try {
      await api.post('/encaisse/mouvement', { ...form, montant: parseFloat(form.montant) });
      onSuccess();
    } catch (err) {
      onError(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box modal-sm">
        <div className="modal-header">
          <h3>Mouvement d'argent</h3>
          <button className="modal-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Date</label>
            <DateInput value={form.date_trans} onChange={v => set('date_trans', v)} />
          </div>
          <div className="field">
            <label>Montant $</label>
            <input type="number" step="0.01" min="0" value={form.montant} onChange={e => set('montant', e.target.value)} placeholder="0.00" />
          </div>
          <div className="mouvement-direction">
            <div className="field">
              <label>De :</label>
              <select value={form.de} onChange={e => set('de', e.target.value)}>
                {LOCALISATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <span className="mouvement-arrow">→</span>
            <div className="field">
              <label>Vers :</label>
              <select value={form.vers} onChange={e => set('vers', e.target.value)}>
                {LOCALISATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          {form.montant > 0 && form.de !== form.vers && (
            <div className="mouvement-preview">
              <div className="preview-label muted">Soldes après mouvement :</div>
              <div>
                <span>{form.de} : </span>
                <span className={soldeDe >= 0 ? 'gold' : 'danger'}>{fmt(soldeDe)}</span>
                <span className="muted"> → </span>
                <span className={aprDe >= 0 ? 'success' : 'danger'}>{fmt(aprDe)}</span>
              </div>
              <div>
                <span>{form.vers} : </span>
                <span className={soldeVers >= 0 ? 'gold' : 'danger'}>{fmt(soldeVers)}</span>
                <span className="muted"> → </span>
                <span className={aprVers >= 0 ? 'success' : 'danger'}>{fmt(aprVers)}</span>
              </div>
            </div>
          )}
          <div className="field">
            <label>Description</label>
            <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="ex. Remboursement avance" />
          </div>
          <div className="field">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={confirmer} disabled={saving || form.de === form.vers}>
            {saving ? 'Enregistrement…' : 'Confirmer le mouvement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL SOLDES DE DÉPART ──────────────────────────────────────────────────

function ModalSoldes({ encaisse, onClose, onSuccess, onError }) {
  const [soldes, setSoldes] = useState(() => {
    const m = {};
    encaisse.soldes.forEach(s => { m[s.localisation] = { solde: s.solde_depart, date: s.date_depart }; });
    return m;
  });
  const [saving, setSaving] = useState(false);

  async function sauvegarder() {
    setSaving(true);
    try {
      for (const loc of LOCALISATIONS) {
        await api.put(`/encaisse/soldes/${encodeURIComponent(loc)}`, {
          solde_depart: parseFloat(soldes[loc]?.solde || 0),
          date_depart:  soldes[loc]?.date || '2026-01-01',
        });
      }
      onSuccess();
    } catch (err) {
      onError(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box modal-sm">
        <div className="modal-header">
          <h3>Soldes de départ</h3>
          <button className="modal-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Date de départ</label>
            <DateInput
              value={soldes[LOCALISATIONS[0]]?.date || '2026-01-01'}
              onChange={v => {
                setSoldes(prev => {
                  const next = { ...prev };
                  LOCALISATIONS.forEach(l => { next[l] = { ...next[l], date: v }; });
                  return next;
                });
              }}
            />
          </div>
          {LOCALISATIONS.map(loc => (
            <div className="field" key={loc}>
              <label>{loc}</label>
              <div className="input-suffix">
                <input
                  type="number" step="0.01"
                  value={soldes[loc]?.solde ?? 0}
                  onChange={e => setSoldes(p => ({ ...p, [loc]: { ...p[loc], solde: e.target.value } }))}
                />
                <span>$</span>
              </div>
            </div>
          ))}
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Ces montants sont le point de départ. Les transactions viennent s'y ajouter.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={sauvegarder} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL IMPORT CSV ─────────────────────────────────────────────────────────

function ModalImportCSV({ onClose, onSuccess, onError }) {
  const [etape, setEtape]     = useState(1); // 1 upload, 2 preview, 3 confirm
  const [fichier, setFichier] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  async function uploader() {
    if (!fichier) return onError('Sélectionner un fichier CSV');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', fichier);
      const data = await api.postForm('/journal/import-csv', fd);
      setPreview(data);
      setEtape(2);
    } catch (err) {
      onError(err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  async function confirmer() {
    if (!preview?.valides?.length) return;
    setLoading(true);
    try {
      const data = await api.post('/journal/import-csv/confirmer', { lignes: preview.valides });
      onSuccess(data.nb_importees);
    } catch (err) {
      onError(err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h3>Import CSV</h3>
          <button className="modal-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
        </div>
        <div className="modal-body">
          {etape === 1 && (
            <>
              <p className="muted" style={{ marginBottom: 12 }}>
                Colonnes attendues : <code>date_trans, associe, categorie, sous_categorie, description, fournisseur, montant, destination, numero_recu, notes</code>
              </p>
              <div className="upload-zone big" onClick={() => fileRef.current?.click()}>
                {fichier
                  ? <span className="success"><Icon name="document" size={14} /> {fichier.name}</span>
                  : <span className="muted">Cliquer ou glisser un fichier CSV ici</span>
                }
              </div>
              <input type="file" ref={fileRef} accept=".csv,.txt" style={{ display: 'none' }}
                onChange={e => setFichier(e.target.files[0] || null)} />
            </>
          )}
          {etape === 2 && preview && (
            <>
              <div className="import-stats">
                <span className="success">{preview.valides.length} ligne(s) valide(s)</span>
                {preview.invalides.length > 0 && (
                  <span className="danger"> · {preview.invalides.length} erreur(s)</span>
                )}
                <span className="muted"> sur {preview.total} total</span>
              </div>
              {preview.invalides.length > 0 && (
                <div className="import-errors">
                  <strong className="danger">Lignes avec erreurs :</strong>
                  {preview.invalides.map(e => (
                    <div key={e.ligne} className="import-error-row">
                      <span className="muted">Ligne {e.ligne} :</span> {e.erreurs.join(', ')}
                    </div>
                  ))}
                </div>
              )}
              {preview.valides.length > 0 && (
                <div className="import-preview">
                  <strong>Aperçu des {Math.min(5, preview.valides.length)} premières lignes :</strong>
                  {preview.valides.slice(0, 5).map((r, i) => (
                    <div key={i} className="import-preview-row">
                      {r.date_trans} · {r.associe} · {r.description} · <span className={parseFloat(r.montant) >= 0 ? 'success' : 'danger'}>{fmt(parseFloat(r.montant))}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          {etape === 1 && (
            <button className="btn btn-primary" onClick={uploader} disabled={loading || !fichier}>
              {loading ? 'Analyse…' : 'Analyser le fichier'}
            </button>
          )}
          {etape === 2 && (
            <>
              <button className="btn btn-ghost" onClick={() => setEtape(1)}>← Retour</button>
              <button className="btn btn-primary" onClick={confirmer} disabled={loading || !preview?.valides?.length}>
                {loading ? 'Import…' : `Importer ${preview?.valides?.length || 0} transaction(s)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
